import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
const SIZE_MAP: Record<string, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-[95vw]'
};
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
}
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth,
  size,
  showCloseButton = true
}: ModalProps) {
  const resolvedMaxWidth = maxWidth || (size ? SIZE_MAP[size] : 'max-w-2xl');
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return createPortal(<div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title' : undefined} data-id="element-287">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} aria-hidden="true" data-id="element-288" />

      <div className={cn('relative w-full bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200', resolvedMaxWidth)} data-id="element-289">
        {(title || showCloseButton) && <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white sticky top-0 z-10" data-id="element-290">
            {title && <h3 id="modal-title" className="text-lg font-semibold text-slate-900" data-id="element-291">
                {title}
              </h3>}
            {showCloseButton && <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close modal" data-id="element-292">
                <X className="w-5 h-5" data-id="element-293" />
              </button>}
          </div>}
        <div className="p-6 overflow-y-auto" data-id="element-294">{children}</div>
      </div>
    </div>, document.body);
}